import { CellContext, EllipsisTextWithTooltip } from '@shape-ds/components';
import AssetBreadCrumbTooltip from 'components/AssetBreadCrumbTooltip/AssetBreadCrumbTooltip';
import { SeverityPills, BuildColumns, buildColumnsObject } from '@shape/lighthouse-components';
import { ActionStatus, CaseListItem, caseTabs } from 'queries/cases/types';
import { useTranslation } from 'react-i18next';
import { dateTimeDay, sortDatesDesc } from 'utils';
import { ExternalReferenceSummary } from '../components/ExternalReference/ExternalReferenceSummary';
import { getTotalExternalReferences } from '../components/ExternalReference/helpers';
import { severityMap } from '../components/Filters/CasesSummaryPanel';
import Alarm30d from '../components/Alarms30d/Alarms30d';
import { formatUserName } from '../../Events/components/Records/utils';
import { useParams } from 'react-router-dom';
import { useCaseContext } from '../Cases.context';
import { ActionStatusCell } from '../components/ActionStatusCell/ActionStatusCell';

const sortString = (a?: string, b?: string) => {
  if (!a || !b) return 0;
  return a.localeCompare(b);
};

const sortDates = (a?: string, b?: string) => {
  if (!a || !b) return 0;
  return sortDatesDesc(a, b);
};
const sortEndDateColumns = (
  a: CellContext<CaseListItem, unknown>['row'],
  b: CellContext<CaseListItem, unknown>['row'],
): number => {
  const dateA = a.original.updated_at || a.original.start_date;
  const dateB = b.original.updated_at || b.original.start_date;
  return sortDates(dateA, dateB);
};

export const getCaseColumns = (isCollapsed?: boolean): BuildColumns<CaseListItem>['columns'] => {
  return {
    severity: {
      enableSorting: false,
      size: 60,
      cell: (row: CellContext<CaseListItem, unknown>) => {
        const severityLevel = row.row.original.severity || '';
        return <SeverityPills severity={severityMap[severityLevel]} />;
      },
    },

    start_date: {
      size: isCollapsed ? 78 : 90,
      sortingFn: (
        a: CellContext<CaseListItem, unknown>['row'],
        b: CellContext<CaseListItem, unknown>['row'],
      ) => {
        return sortDates(a.original.start_date, b.original.start_date);
      },
      cell: (row: CellContext<CaseListItem, unknown>) => {
        return dateTimeDay(row.row.original.start_date);
      },
    },

    asset: {
      sortingFn: (
        a: CellContext<CaseListItem, unknown>['row'],
        b: CellContext<CaseListItem, unknown>['row'],
      ) => {
        return sortString(a.original.asset?.name, b.original.asset?.name);
      },
      cell: (row: CellContext<CaseListItem, unknown>) => {
        if (!row.row.original.asset) return;

        return (
          <AssetBreadCrumbTooltip
            assetName={row.row.original.asset.name}
            key={row.row.original.asset.id}
          />
        );
      },
    },

    dimension: {
      size: 114,
      enableColumnFilter: true,
      i18nkey: 'case:dimension',
    },

    discipline: {
      size: 100,
      enableColumnFilter: true,
      i18nkey: 'case:discipline',
    },

    name: {
      enableSorting: false,
    },

    action_status: {
      size: 112,
      enableColumnFilter: true,
      cell: (row: CellContext<CaseListItem, unknown>) => (
        <ActionStatusCell
          actions={row.row.original.actions}
          actionStatus={row.row.original.action_status as ActionStatus}
        />
      ),
      i18nkey: 'case:status_count_panel.status.action_status',
    },
    external_reference_summary: {
      sortingFn: (
        a: CellContext<CaseListItem, unknown>['row'],
        b: CellContext<CaseListItem, unknown>['row'],
      ) => {
        const aSummary = getTotalExternalReferences(a.original.external_reference_summary);
        const bSummary = getTotalExternalReferences(b.original.external_reference_summary);
        return aSummary - bSummary;
      },
      size: 80,
      cell: (row: CellContext<CaseListItem, unknown>) => {
        const { external_reference_summary } = row.row.original;
        return <ExternalReferenceSummary summary={external_reference_summary} />;
      },
    },
    alarms_30_days: {
      size: 96,
      sortingFn: (
        a: CellContext<CaseListItem, unknown>['row'],
        b: CellContext<CaseListItem, unknown>['row'],
      ) => {
        const aAlarms = a.original.alarms_30_days?.quantity || 0;
        const bAlarms = b.original.alarms_30_days?.quantity || 0;
        return aAlarms - bAlarms;
      },
      cell: (row: CellContext<CaseListItem, unknown>) => {
        return <Alarm30d alarmsInfo={row.row.original.alarms_30_days} />;
      },
    },
    owner_mail: {
      size: isCollapsed ? 80 : 155,
      cell: (row: CellContext<CaseListItem, unknown>) => {
        const { owner_mail } = row.row.original;
        if (!owner_mail) return null;

        return (
          <EllipsisTextWithTooltip
            overrideTooltipText={owner_mail}
            text={formatUserName(owner_mail)}
          />
        );
      },
    },

    status: {
      size: 80,
      i18nkey: 'case:status',
      enableColumnFilter: true,
    },
    updated_at: {
      size: 95,
      sortingFn: sortEndDateColumns,
      cell: (row: CellContext<CaseListItem, unknown>) => {
        const { start_date, updated_at } = row.row.original;
        const dt = updated_at || start_date;
        return dateTimeDay(dt, true);
      },
    },
  };
};

const defaultColumns = [
  'severity',
  'start_date',
  'asset',
  'name',
  'dimension',
  'discipline',
  'action_status',
  'external_reference_summary',
  'alarms_30_days',
  'owner_mail',
  'status',
  'updated_at',
];
const CASE_CLOSED_COLUMNS = [
  'severity',
  'start_date',
  'asset',
  'name',
  'dimension',
  'discipline',
  'status',
  'alarms_30_days',
  'updated_at',
  'owner_mail',
];

const getOverrideColumns = (isCaseSelected: boolean, status: string) => {
  if (!isCaseSelected && status === caseTabs.closed) return CASE_CLOSED_COLUMNS;
  if (!isCaseSelected) return defaultColumns;

  switch (status) {
    case caseTabs.open:
      return [
        'severity',
        'start_date',
        'asset',
        'name',
        'discipline',
        'external_reference_summary',
        'alarms_30_days',
        'updated_at',
      ];
    case caseTabs.closed:
      return ['severity', 'start_date', 'asset', 'name', 'discipline', 'updated_at'];
    case caseTabs.all:
      return [
        'severity',
        'start_date',
        'asset',
        'name',
        'discipline',
        'status',
        'action_status',
        'updated_at',
      ];
    default:
      return defaultColumns;
  }
};

export const useCaseColumns = (isCollapsed?: boolean, overrideColumns?: string[]) => {
  const { t } = useTranslation();
  const { status = caseTabs.open } = useParams();
  const { selectedCase } = useCaseContext();

  const statusOverrideColumns = getOverrideColumns(!!selectedCase, status as string);
  return {
    columns: buildColumnsObject<CaseListItem>({
      columns: getCaseColumns(isCollapsed),
      getLabel: (key) =>
        key === 'updated_at' && status === caseTabs.closed
          ? t(`case:common.closed_date`)
          : t(`case:common.${key}`),
      overrideColumns: overrideColumns ?? statusOverrideColumns,
    }),
  };
};
